import React, { useState, useEffect, useCallback } from 'react'
import { supabase } from '../lib/supabase'
import { getPasswordResetRedirectUrl } from '../lib/authRedirects'
import CreateCustomerModal from './CreateCustomerModal'
import InviteCustomerModal from './InviteCustomerModal'
import ToggleSwitch from './ToggleSwitch'
import AutomationIntegrationsModal from './AutomationIntegrationsModal'

const CustomerManagement = () => {
  const [customers, setCustomers] = useState([])
  const [loading, setLoading] = useState(true)
  const [searchTerm, setSearchTerm] = useState('')
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [showInviteModal, setShowInviteModal] = useState(false)
  const [error, setError] = useState('')
  const [successMessage, setSuccessMessage] = useState('')
  const [currentAdmin, setCurrentAdmin] = useState(null)
  const [automationLoading, setAutomationLoading] = useState({})
  const [automationModalCustomer, setAutomationModalCustomer] = useState(null)
  const [pendingDelete, setPendingDelete] = useState(null)
  const [deleteLoading, setDeleteLoading] = useState(false)
  const [showAutomationModal, setShowAutomationModal] = useState(false)

  const fetchCustomers = useCallback(async () => {
    try {
      setLoading(true)
      const { data, error } = await supabase
        .from('businesses')
        .select(`
          *,
          users!businesses_owner_email_fkey (
            id,
            email,
            role,
            created_at
          ),
          automation_profile:business_automation_profiles (
            is_enabled,
            enabled_at,
            enabled_by
          )
        `)
        .order('created_at', { ascending: false })

      if (error) throw error
      const normalized = (data || []).map((customer) => {
        const userRelation = Array.isArray(customer.users)
          ? customer.users[0]
          : customer.users
        const automationData = Array.isArray(customer.automation_profile)
          ? customer.automation_profile[0]
          : customer.automation_profile
        return {
          ...customer,
          auth_user_id: userRelation?.id || null,
          automation_profile:
            automationData ||
            {
              business_id: customer.id,
              is_enabled: false,
              enabled_at: null,
              enabled_by: null,
            },
        }
      })

      setCustomers(normalized)
    } catch (err) {
      setError('Failed to fetch customers')
      console.error('Error fetching customers:', err)
    } finally {
      setLoading(false)
    }
  }, [])

  const initialize = useCallback(async () => {
    const { data: currentUser } = await supabase.auth.getUser()
    if (currentUser?.user) {
      setCurrentAdmin(currentUser.user)
    }
    await syncBusinessesForExistingCustomers(currentUser?.user)
    await fetchCustomers()
  }, [fetchCustomers])

  useEffect(() => {
    initialize()
  }, [initialize])

  useEffect(() => {
    const channel = supabase
      .channel('admin-businesses')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'businesses'
        },
        () => {
          fetchCustomers()
        }
      )

    channel.subscribe()

    return () => {
      supabase.removeChannel(channel)
    }
  }, [fetchCustomers])


  // For any existing users with role = 'customer' that do not yet have a corresponding
  // row in the businesses table, create a simple business record so they appear in
  // the Customer Management view. This primarily helps when customer users were
  // created manually (e.g. via SQL) without using the in-app "Add Customer" flow.
  const syncBusinessesForExistingCustomers = async (adminUserParam) => {
    try {
      let adminUser = adminUserParam
      if (!adminUser) {
        const { data } = await supabase.auth.getUser()
        adminUser = data?.user
        if (adminUser) {
          setCurrentAdmin(adminUser)
        }
      }

      if (!adminUser) {
        return
      }

      // Fetch all users who are marked as customers
      const { data: customerUsers, error: usersError } = await supabase
        .from('users')
        .select('email')
        .eq('role', 'customer')

      if (usersError) {
        console.error('Error fetching customer users for sync:', usersError)
        return
      }

      if (!customerUsers || customerUsers.length === 0) return

      // Fetch all existing businesses to see which owner_email values already exist
      const { data: businessRows, error: businessesError } = await supabase
        .from('businesses')
        .select('owner_email')

      if (businessesError) {
        console.error('Error fetching businesses for sync:', businessesError)
        return
      }

      const existingOwners = new Set((businessRows || []).map((b) => b.owner_email))

      const inserts = customerUsers
        .filter((u) => u.email && !existingOwners.has(u.email))
        .map((u) => ({
          business_name: u.email.split('@')[0] || u.email,
          owner_email: u.email,
          admin_id: adminUser.id,
        }))

      if (inserts.length > 0) {
        const { data: newBusinesses, error: insertError } = await supabase
          .from('businesses')
          .insert(inserts)
          .select('id')

        if (insertError) {
          console.error('Error creating businesses for existing customers:', insertError)
        }

        if (newBusinesses && newBusinesses.length > 0) {
          const automationSeed = newBusinesses.map((biz) => ({
            business_id: biz.id,
            is_enabled: false,
            enabled_at: null,
            enabled_by: null,
          }))

          const { error: automationSeedError } = await supabase
            .from('business_automation_profiles')
            .upsert(automationSeed, { onConflict: 'business_id' })

          if (automationSeedError) {
            console.error('Error seeding automation profiles:', automationSeedError)
          }
        }
      }
    } catch (err) {
      console.error('Unexpected error during customer-business sync:', err)
    }
  }

  const handleToggleStatus = async (customerId, currentStatus) => {
    try {
      const newStatus = currentStatus === 'active' ? 'inactive' : 'active'
      
      const { error } = await supabase
        .from('businesses')
        .update({ 
          status: newStatus,
          activated_at: newStatus === 'active' ? new Date().toISOString() : null
        })
        .eq('id', customerId)

      if (error) throw error

      // Refresh the list
      await fetchCustomers()
      setSuccessMessage(`Customer status updated to ${newStatus}`)
      setTimeout(() => setSuccessMessage(''), 3000)
    } catch (err) {
      setError('Failed to update customer status')
    }
  }

  const handleAutomationToggle = async (customer, nextValue) => {
    try {
      let adminUser = currentAdmin
      if (!adminUser) {
        const { data } = await supabase.auth.getUser()
        adminUser = data?.user
        if (adminUser) {
          setCurrentAdmin(adminUser)
        }
      }

      if (!adminUser) {
        setError('You must be logged in as an admin to update automation status')
        return
      }

      setAutomationLoading((prev) => ({ ...prev, [customer.id]: true }))

      const payload = {
        business_id: customer.id,
        is_enabled: nextValue,
        enabled_at: nextValue ? new Date().toISOString() : null,
        enabled_by: nextValue ? adminUser.id : null,
      }

      const { error: automationError } = await supabase
        .from('business_automation_profiles')
        .upsert(payload, { onConflict: 'business_id' })

      if (automationError) {
        throw automationError
      }

      setCustomers((prev) =>
        prev.map((row) =>
          row.id === customer.id
            ? {
                ...row,
                automation_profile: payload,
              }
            : row
        )
      )
      setError('')

      setSuccessMessage(`Automation ${nextValue ? 'enabled' : 'disabled'} successfully`)
      setTimeout(() => setSuccessMessage(''), 3000)
    } catch (err) {
      console.error('Failed to toggle automation:', err)
      setError('Failed to update automation status')
      setTimeout(() => setError(''), 4000)
    } finally {
      setAutomationLoading((prev) => ({ ...prev, [customer.id]: false }))
    }
  }
  const handleSendPasswordReset = async (email) => {
    try {
      const passwordResetRedirectTo = getPasswordResetRedirectUrl()
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: passwordResetRedirectTo,
      })
      if (error) throw error
      
      alert(`Password reset email sent to ${email}`)
    } catch (err) {
      setError(`Failed to send password reset: ${err.message}`)
    }
  }

  const confirmDeleteCustomer = async () => {
    if (!pendingDelete) return

    setDeleteLoading(true)

    try {
      // Server-side deletion (SECURITY DEFINER) so we can remove both:
      // - auth.users (Supabase Auth)
      // - public.users/public.businesses (+ cascades)
      const { data, error } = await supabase.rpc('admin_delete_customer', {
        p_email: pendingDelete.email,
      })

      if (error) throw error
      if (!data?.ok) {
        throw new Error('Delete failed')
      }

      setCustomers((prev) => prev.filter((customer) => customer.id !== pendingDelete.id))
      setSuccessMessage('Customer deleted successfully')
      setTimeout(() => setSuccessMessage(''), 4000)
      setPendingDelete(null)
    } catch (err) {
      console.error('Failed to delete customer:', err)
      setError(`Failed to delete customer: ${err.message}`)
      setTimeout(() => setError(''), 4000)
    } finally {
      setDeleteLoading(false)
    }
  }

  const filteredCustomers = customers.filter(customer =>
    customer.business_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    customer.owner_email?.toLowerCase().includes(searchTerm.toLowerCase())
  )

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    )
  }

  return (
    <div className="bg-white shadow rounded-lg">
      <div className="px-6 py-4 border-b border-gray-200">
        <div className="flex justify-between items-center">
          <h3 className="text-lg font-medium text-gray-900">Customer Management</h3>
          <div className="flex space-x-3">
            <button
              onClick={() => setShowInviteModal(true)}
              className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-md text-sm font-medium"
            >
              Invite Customer
            </button>
            <button
              onClick={() => setShowCreateModal(true)}
              className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-md text-sm font-medium"
            >
              Add Customer
            </button>
          </div>
        </div>
        
        <div className="mt-4">
          <input
            type="text"
            placeholder="Search customers..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="block w-full max-w-xs border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
          />
        </div>
      </div>

      {error && (
        <div className="px-6 py-3 bg-red-50 border-l-4 border-red-400">
          <div className="text-sm text-red-700">{error}</div>
        </div>
      )}

      {successMessage && (
        <div className="px-6 py-3 bg-green-50 border-l-4 border-green-400">
          <div className="text-sm text-green-700">{successMessage}</div>
        </div>
      )}

      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Business
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Email
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Created
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Status
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Automation
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {filteredCustomers.length === 0 ? (
              <tr>
                <td colSpan="6" className="px-6 py-4 text-center text-sm text-gray-500">
                  {searchTerm ? 'No customers match your search.' : 'No customers found. Create your first customer!'}
                </td>
              </tr>
            ) : (
              filteredCustomers.map((customer) => (
                <tr key={customer.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900">
                      {customer.business_name}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-500">{customer.owner_email}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-500">
                      {new Date(customer.created_at).toLocaleDateString()}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                      customer.status === 'active' 
                        ? 'bg-green-100 text-green-800' 
                        : customer.status === 'inactive'
                        ? 'bg-yellow-100 text-yellow-800'
                        : 'bg-red-100 text-red-800'
                    }`}>
                      {customer.status || 'Active'}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center space-x-3">
                      <ToggleSwitch
                        id={`automation-${customer.id}`}
                        checked={customer.automation_profile?.is_enabled || false}
                        onChange={(value) => handleAutomationToggle(customer, value)}
                        compact
                        className="items-center"
                        disabled={!!automationLoading[customer.id]}
                      />
                      <span className="text-sm text-gray-600">
                        {customer.automation_profile?.is_enabled ? 'On' : 'Off'}
                      </span>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <div className="flex space-x-2">
                      <button
                        onClick={() => handleToggleStatus(customer.id, customer.status || 'active')}
                        className={`${
                          (customer.status || 'active') === 'active' 
                            ? 'text-yellow-600 hover:text-yellow-900' 
                            : 'text-green-600 hover:text-green-900'
                        }`}
                        title={`${(customer.status || 'active') === 'active' ? 'Deactivate' : 'Activate'} customer`}
                      >
                        {(customer.status || 'active') === 'active' ? 'Deactivate' : 'Activate'}
                      </button>
                      <button
                        onClick={() => {
                          setAutomationModalCustomer(customer)
                          setShowAutomationModal(true)
                        }}
                        className="text-indigo-600 hover:text-indigo-900"
                        title="Manage automations"
                      >
                        Automations
                      </button>
                      <button
                        onClick={() => handleSendPasswordReset(customer.owner_email)}
                        className="text-blue-600 hover:text-blue-900"
                        title="Send password reset"
                      >
                        Reset Password
                      </button>
                      <button
                        onClick={() =>
                          setPendingDelete({
                            id: customer.id,
                            email: customer.owner_email,
                            name: customer.business_name,
                            authUserId: customer.auth_user_id,
                          })
                        }
                        className="text-red-600 hover:text-red-900"
                        title="Delete customer"
                      >
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <CreateCustomerModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onSuccess={(newBusiness) => {
          // Optimistically add the new business to the table for immediate feedback
          if (newBusiness) {
            setCustomers((prev) => [newBusiness, ...prev])
          }
          // Also re-fetch from Supabase to ensure full synchronization
          fetchCustomers()
          setError('')
          setSuccessMessage('Customer created successfully')
          setTimeout(() => setSuccessMessage(''), 5000)
        }}
      />

      <InviteCustomerModal
        isOpen={showInviteModal}
        onClose={() => setShowInviteModal(false)}
        onSuccess={(result) => {
          fetchCustomers()
          setError('')
          if (result.emailSent) {
            setSuccessMessage(`Invitation sent to ${result.email}`)
          } else {
            setSuccessMessage(`Customer created for ${result.email}. Temporary password: ${result.tempPassword}`)
          }
          setTimeout(() => setSuccessMessage(''), 5000)
        }}
      />

      <AutomationIntegrationsModal
        isOpen={showAutomationModal}
        customer={automationModalCustomer}
        onClose={() => {
          setShowAutomationModal(false)
          setAutomationModalCustomer(null)
        }}
      />

      {pendingDelete && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Delete customer?</h3>
            <p className="text-sm text-gray-600 mb-4">
              Are you sure you'd like to delete the profile for{' '}
              <span className="font-medium">{pendingDelete.name || pendingDelete.email}</span>? This action cannot be undone.
            </p>
            <div className="flex justify-end gap-3">
              <button
                type="button"
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
                disabled={deleteLoading}
                onClick={() => setPendingDelete(null)}
              >
                Cancel
              </button>
              <button
                type="button"
                className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-md hover:bg-red-700 disabled:opacity-50"
                onClick={confirmDeleteCustomer}
                disabled={deleteLoading}
              >
                {deleteLoading ? 'Deleting…' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default CustomerManagement